import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { taxPaymentSettingsGateway } from '../index';
import type { TaxPaymentSettingsOverview } from '../types';
import { SETUP_STATUSES } from '../types';
import { filterRows, statusChipLabel, statusCounts, statusTone, toListRows, type StatusFilter, type TaxListRow } from '../taxPaymentView';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { JurisdictionDrawer } from './JurisdictionDrawer';
import '../tax-payment-settings.css';

/** Tax Payment Settings: one row per jurisdiction and work location, filtered by the setup status EPN can actually derive, with a right-side
 * drawer for the details. The statuses never say Ready or Verified: EPN records what the employer enters and does not verify it. */
export function TaxPaymentSettingsPage() {
  const [overview, setOverview] = useState<TaxPaymentSettingsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<TaxListRow | null>(null);
  /** The tax component whose chip opened the drawer, so it lands on that component's tab. */
  const [openComponent, setOpenComponent] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const lastRowRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    taxPaymentSettingsGateway
      .getOverview(controller.signal)
      .then((result) => { setOverview(result); setError(null); })
      .catch((err) => { if (!controller.signal.aborted) setError(friendlyErrorMessage(err, 'Could not load Tax Payment Settings.')); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [reloadKey]);

  const rows = useMemo(() => (overview ? toListRows(overview) : []), [overview]);
  const counts = useMemo(() => statusCounts(rows), [rows]);
  const visible = useMemo(() => filterRows(rows, status, search), [rows, status, search]);

  // The server stays the judge of who may change entries; this only avoids offering buttons that would be refused.
  const canManage = true;

  const openRow = (row: TaxListRow, element: HTMLTableRowElement, component: string | null = null) => {
    lastRowRef.current = element;
    setOpenComponent(component);
    setOpen(row);
  };
  const close = useCallback(() => {
    setOpen(null);
    // Entries may have changed the row's status; refresh the list in place without touching the filters, search or scroll.
    setReloadKey((k) => k + 1);
    requestAnimationFrame(() => lastRowRef.current?.focus());
  }, []);
  const onRowKey = (row: TaxListRow) => (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openRow(row, event.currentTarget);
    }
  };

  const filters: StatusFilter[] = ['all', ...SETUP_STATUSES];

  return (
    <section className="tps-page">
      <h2 className="tps-page__title">Tax Payment Settings</h2>

      <div className="tps-card">
        <div className="tps-toolbar">
          <div className="tps-filters" role="group" aria-label="Filter by setup status">
            {filters.map((f) => (
              <button
                key={f} type="button" className={`tps-filter${status === f ? ' tps-filter--active' : ''}${f === 'all' ? '' : ` tps-filter--${statusTone(f)}`}`} aria-pressed={status === f}
                onClick={() => setStatus(f)}
              >
                {statusChipLabel(f)} <span className="tps-count" aria-label={`${counts[f] ?? 0} locations`}>{counts[f] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="tps-search">
            <span className="tps-ico tps-ico--search" aria-hidden="true" />
            <label htmlFor="tps-search" className="tps-sr-only">Search jurisdiction, work location or address</label>
            <input id="tps-search" type="search" placeholder="Search jurisdiction, location, address…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        {loading && <p role="status">Loading…</p>}
        {error && <p className="tps-error" role="alert">{error}</p>}

        {overview && (
          <div className="tps-table-wrap">
            <table className="tps-list" aria-label="Tax jurisdictions and their setup status">
              <thead>
                <tr>
                  <th scope="col">Jurisdiction</th>
                  <th scope="col">Work Location</th>
                  <th scope="col">Tax Setup Type</th>
                  <th scope="col">No. of Employees</th>
                  <th scope="col">Setup Status</th>
                  <th scope="col">Last Verified</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr
                    key={row.key} className="tps-row" tabIndex={0} aria-label={`Open ${row.jurisdiction} ${row.workLocationName} tax setup`}
                    onClick={(e) => openRow(row, e.currentTarget)} onKeyDown={onRowKey(row)}
                  >
                    <td>
                      <div className="tps-strong">{row.jurisdiction}</div>
                      {row.subtitle && <div className="tps-muted tps-small">{row.subtitle}</div>}
                    </td>
                    <td>
                      <div className="tps-strong">{row.workLocationName}</div>
                      {row.address && <div className="tps-muted tps-small">{row.address}</div>}
                    </td>
                    <td>
                      <span className="tps-chips">
                        {row.chips.map((c) => row.source.hasDetailedSetup ? (
                          <button
                            key={c} type="button" className="tps-chip tps-chip--action" aria-label={`Open ${row.jurisdiction} ${row.workLocationName} ${c} setup`}
                            onClick={(e) => { e.stopPropagation(); const rowEl = e.currentTarget.closest('tr'); if (rowEl) openRow(row, rowEl, c); }}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {c}
                          </button>
                        ) : <span key={c} className="tps-chip">{c}</span>)}
                      </span>
                    </td>
                    <td>{row.employees}</td>
                    <td><span className={`tps-pill tps-pill--${statusTone(row.status)}`}>{row.status}</span></td>
                    <td>{row.verifiedOn ?? <span className="tps-muted">Not verified</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visible.length === 0 && <p className="tps-muted tps-empty" role="status">No locations match this filter or search.</p>}
          </div>
        )}

        {overview && overview.rows.length === 1 && (
          <p className="tps-muted">Add a work location in Business Profile to see the state it is in. Only the federal row is shown until then.</p>
        )}

        {overview && overview.locationsNeedingReview.length > 0 && (
          <div role="note" className="tps-review">
            <h3>Locations that need a closer look</h3>
            <ul>
              {overview.locationsNeedingReview.map((l) => <li key={l.id}><strong>{l.name}:</strong> {l.reason}</li>)}
            </ul>
          </div>
        )}
      </div>

      {open && <JurisdictionDrawer row={rows.find((r) => r.key === open.key) ?? open} onSetupChanged={() => setReloadKey((k) => k + 1)} initialComponent={openComponent} canManage={canManage} einLast4={overview?.companyEinLast4 ?? null} onClose={close} />}
    </section>
  );
}
